import Image from 'next/image';
import Router from 'next/router';
import { memo, useCallback, useRef } from 'react';

import { useTrackClickedPublicBoardLink } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { Tooltip } from '@air/primitive-tooltip';
import { useShortIdContext } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

import { usePublicShareWorkspaceAccess } from '~/components/PublicShareWorkspaceAccessProvider';
import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { PublicShowcaseSearch } from '~/components/Search/PublicShowcaseSearch';
import { usePublicShowcaseHeaderScrollOpacity } from '~/components/v2/components/showcase/hooks/usePublicShowcaseHeaderScrollOpacity';
import { usePublicShowcaseImmersiveCover } from '~/components/v2/components/showcase/hooks/usePublicShowcaseImmersiveCover';
import { PublicShowcaseNavMenu } from '~/components/v2/components/showcase/PublicShowcaseNavMenu';
import { SHOWCASE_PAGE_COLUMN_CLASS_NAME } from '~/components/v2/components/showcase/showcasePageColumn';
import { createCollectionRoute } from '~/constants/routes';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { usePublicCustomLogo } from '~/hooks/usePublicCustomLogo';

// The surface opacity at which the header's own colors become valid again (see the workspace-name
// span below).
const FULLY_PAINTED_SURFACE_OPACITY = 1;

// Collection-owned fork of the shared PublicWorkspaceHeader (used by public board pages). The
// collection page constrains the header content to its content column and owns its own Search and
// workspace-navigate behavior, so those differences live here instead of leaking props into the
// shared board header. Fully smart: it reads its own context, so the page threads nothing.
export const PublicShowcaseWorkspaceHeader = memo(() => {
  const { presentationSettings } = useShortIdContext();
  const { workspaceImage, workspaceName } = usePublicWorkspace();
  const { customLogoUrl } = usePublicCustomLogo();
  const { headerBgColor, headerForegroundColor, pageBgColor } = usePublicCustomColors();
  const { viewerIsMemberOfPublicWorkspace } = usePublicShareWorkspaceAccess();
  const { trackClickedPublicBoardLink } = useTrackClickedPublicBoardLink();
  const { isSearchActive } = useIsSearchActive();

  // The fade resolves the scroll root it tracks by walking UP from this element, so the header's own
  // DOM position decides which scroller it reads — the collection link editor nests this page inside
  // the private app shell, which has a scroll area of its own carrying the same id.
  const headerRef = useRef<HTMLDivElement>(null);

  // An immersive cover bleeds under this header (the page pulls the scroll area up behind it), so
  // the header starts with a transparent surface and lets the cover image show through. The SAME
  // reading the page composes the immersive layout from — so the header can never suppress its
  // surface over a page that is rendering no cover.
  const { hasImmersiveCover } = usePublicShowcaseImmersiveCover();

  // FOUND-598: the surface dissolves IN as the cover scrolls away instead of staying transparent
  // forever — otherwise the nav elements float unbacked over the page content below the cover.
  const { headerSurfaceOpacity } = usePublicShowcaseHeaderScrollOpacity({
    enabled: hasImmersiveCover,
    headerRef,
  });

  // The header's own colors (its derived foreground below) are only valid against a surface that is
  // actually there — see the span's comment.
  const isHeaderSurfacePainted = headerSurfaceOpacity >= FULLY_PAINTED_SURFACE_OPACITY;

  // Every header HAS a background, so every header has something to dissolve in: the author's custom
  // nav color when they picked one, otherwise the page background this header is transparently
  // stacked on top of — resolved the same way the page resolves it (a custom page color, else the
  // gray-4 token, which the surface carries as a class so it re-resolves per theme island; see
  // PublicShowcasePage's page root and its body background). An immersive link whose author never
  // chose a nav color used to render no surface at all and left the logo, name and nav floating
  // unbacked over the page content forever once the cover had scrolled away.
  const headerSurfaceColor = headerBgColor ?? pageBgColor;

  const handleWorkspaceNavigate = useCallback(() => {
    void Router.push(createCollectionRoute());
    trackClickedPublicBoardLink({
      name: 'clicked view in collection',
      page_area: 'header',
      page_type: 'public collection',
    });
  }, [trackClickedPublicBoardLink]);

  const workspaceInfo = (
    <div className="relative z-30 flex items-center gap-1 px-2" data-testid="PUBLIC_COLLECTION_WORKSPACE_HEADER_INFO">
      {customLogoUrl ? (
        // No gray backing box, border, or corner rounding here: custom brand marks are often
        // transparent PNGs and frequently wide (horizontal wordmarks), so the logo renders whole at
        // a fixed height with auto width instead of cropped into the square avatar box (the
        // workspace-avatar branch below keeps the square plate as a placeholder surface).
        // max-w brackets are proto-fidelity values (mattq/proto-presentation-mode branded header) —
        // no configured max-w steps exist near them. `unoptimized` skips the next/image resizer,
        // whose fixed-size renditions would soften wide marks scaled by CSS; height/width are only
        // the intrinsic-ratio hint.
        <Image
          alt={`Logo for ${workspaceName}`}
          className="h-10 w-auto max-w-[180px] shrink-0 object-contain object-left sm:max-w-[280px]"
          height={40}
          src={customLogoUrl}
          unoptimized
          width={240}
        />
      ) : (
        <>
          <div className="relative size-10 shrink-0 overflow-hidden rounded-8 border border-gray-5 bg-gray-5">
            {!!workspaceImage && (
              <Image
                alt={`Logo for ${workspaceName}`}
                className="object-cover"
                fill
                sizes="80px"
                src={workspaceImage}
              />
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* headerForegroundColor is derived from headerBgColor for contrast, so it must never
                outlive the background it was computed against: it applies only once the surface
                behind this text is FULLY painted (opacity 1 — immediately for a header in normal
                flow, at the end of the immersive fade). While the surface is still translucent the
                text is sitting on the cover image, where no color computed from headerBgColor is
                reliable, so it falls back to the default text-gray-11 token. A stepped handoff at
                full opacity rather than a crossfade: interpolating between two colors would need a
                blend on every scroll frame, and `transition-colors` smooths the one step for free. */}
            <span
              className="truncate font-semibold text-16 text-gray-11 transition-colors"
              style={headerForegroundColor && isHeaderSurfacePainted ? { color: headerForegroundColor } : undefined}
            >
              {workspaceName}
            </span>
          </div>
        </>
      )}
    </div>
  );

  return (
    // Two layers: the surface is its own absolutely-positioned element BEHIND the header row, so
    // fading it in on scroll never touches the opacity of the logo, name, search, or nav menu above
    // it (a fade on the <header> itself would dissolve its contents with it). The row keeps the
    // header's flow box; the wrapper carries the shrink-0 the page's flex column relies on.
    <div className="relative w-full shrink-0" ref={headerRef}>
      <div
        // bg-gray-4 is the default, and it is a CLASS rather than an inline var so the token
        // re-resolves against the nearest `data-theme` island (the editor scopes the link's Page
        // theme to its canvas). An authored color arrives as the inline background-color below, which
        // wins over the class; with none, the style carries no background-color at all and the class
        // shows through — which is exactly what a header with no authored color already looked like,
        // being transparent over this same page background.
        className="pointer-events-none absolute inset-0 bg-gray-4"
        data-testid="PUBLIC_COLLECTION_WORKSPACE_HEADER_SURFACE"
        // Transition length is short enough to smooth the per-frame scroll steps without lagging
        // behind the scroll itself.
        style={{ backgroundColor: headerSurfaceColor, opacity: headerSurfaceOpacity, transition: 'opacity 120ms ease' }}
      />
      {/* `relative` with NO z-index deliberately: positioned siblings without one paint in DOM
          order, so the row is above the surface layer for free — while a z-index here would lift the
          whole header above the search command's portaled popover (which has none), leaving the
          trigger painted over the popover's own input. */}
      <header
        className="relative flex h-18 items-center justify-center"
        data-testid="PUBLIC_COLLECTION_WORKSPACE_HEADER"
      >
        {/* The content column wrapper re-creates the header's flex row inside the constrained box so
          the three flex-1/center sections keep their proportions; `justify-center` on the
          full-width <header> centers the box while the header background stays full-bleed. */}
        <div className={cn('flex w-full items-center', SHOWCASE_PAGE_COLUMN_CLASS_NAME)}>
          <div className="flex min-w-min flex-1 justify-start">
            {viewerIsMemberOfPublicWorkspace ? (
              <Tooltip label="View in workspace">
                <Button
                  className="flex cursor-pointer items-center justify-start hover:bg-transparent"
                  appearance="ghost"
                  onClick={handleWorkspaceNavigate}
                >
                  {workspaceInfo}
                </Button>
              </Tooltip>
            ) : (
              // Mirror the ghost Button's box (h-8 px-2.5) so when the async workspace-member check resolves
              // and this wrapper swaps to the navigable Button, the logo doesn't shift horizontally.
              <div className="flex h-8 items-center justify-start px-2.5">{workspaceInfo}</div>
            )}
          </div>

          <div className="flex justify-center px-4">
            {/* The width needs to be set here otherwise the child element will shrink to the smallest possible width */}
            <div className="w-[600px]">{!presentationSettings?.hideSearchBar && <PublicShowcaseSearch />}</div>
          </div>

          {/* The collection page has no header actions, but the right-hand section (with the same
              inner px-4 box the shared header uses) keeps the three-region flex proportions so the
              Search column stays centered. Below the medium breakpoint the sub-page nav collapses
              into a hamburger menu here (the sticky desktop bar is hidden then); it renders nothing
              on desktop and when the link has no sub-pages, so the region stays empty as before.
              Search-gated because the two nav treatments appear/disappear together (see
              usePublicShowcaseNavEntries): the page strips the desktop bar on the results page,
              and this header — which the results page keeps — must strip the menu with it. */}
          <div className="flex min-w-min flex-1 justify-end gap-2">
            <div className="flex gap-2 px-4">{!isSearchActive && <PublicShowcaseNavMenu />}</div>
          </div>
        </div>
      </header>
    </div>
  );
});

PublicShowcaseWorkspaceHeader.displayName = 'PublicShowcaseWorkspaceHeader';
